import { Tooltip } from 'antd'
import { useEffect, useState } from 'react'
import { ExclamationIcon } from '@heroicons/react/outline'
import { TimesCircleIcon } from '../../../components/Icons/TimesCircleIcon'
import { CheckCircleIcon } from '../../../components/Icons/CheckCircleIcon'
import i18n from '../../../translations'

// Each code's message is picking_tasks.order_errors.<code>; codes with hasConflictingOrders
// interpolate {{orders}} and have a <code>_no_order sibling for a payload without one.
const ApiErrorCodes = [
  {
    code: 'missing_stock',
    showForRightColumn: true,
    showForLeftColumn: true,
  },
  {
    code: 'excessive_stock',
    showForRightColumn: true,
    showForLeftColumn: true,
  },
  {
    code: 'expired_stock',
    showForRightColumn: true,
    showForLeftColumn: true,
  },
  {
    code: 'holded_stock',
    showForRightColumn: true,
    showForLeftColumn: true,
  },
  {
    code: 'invalid_order_status',
    showForRightColumn: true,
    showForLeftColumn: true,
  },
  {
    code: 'sxkm_order',
    showForRightColumn: true,
    showForLeftColumn: true,
  },
  {
    code: 'not_apt_for_single_item_picking',
    showForRightColumn: true,
    showForLeftColumn: false,
  },
  {
    code: 'single_item_picking_conflict',
    hasConflictingOrders: true,
    showForRightColumn: true,
    showForLeftColumn: false,
  },
  {
    code: 'single_item_picking_store_conflict',
    hasConflictingOrders: true,
    showForRightColumn: true,
    showForLeftColumn: false,
  },
  {
    code: 'already_has_picking_order',
    showForRightColumn: true,
    showForLeftColumn: true,
  },
  {
    code: 'pending_container_break',
    showForRightColumn: true,
    showForLeftColumn: true,
  },
]

export const showErrorForColumnDirection = (errorCode, direction) => {
  const errorDef = getErrorDefinition(errorCode)
  if (direction === 'left') {
    return errorDef.showForLeftColumn
  }
  return errorDef.showForRightColumn
}

const getErrorDefinition = errorCode => {
  const errorDef = ApiErrorCodes.find(errorCodeDef => errorCodeDef.code === errorCode)
  if (!errorDef) {
    throw `getErrorDefinition(): unexpected error code "${errorCode}"`
  }
  return errorDef
}

const errorMessage = (code, options) => i18n.t(`picking_tasks.order_errors.${code}`, options)

// Safe, non-throwing lookup of the human-friendly (localized) message for a single
// backend error object ({ error_type, conflicting_orders }). Returns null for
// unknown codes so callers can fall back to the raw code/detail. Shared with
// PickingOrderErrorsPanel so both the legacy grouping tooltip and the
// multi-picker confirm panel translate the same codes (e.g. invalid_order_status).
export const getFriendlyErrorMessage = error => {
  const def = ApiErrorCodes.find(d => d.code === error?.error_type)
  if (!def) return null
  if (def.hasConflictingOrders) {
    const nums = Array.isArray(error?.conflicting_orders) ? error.conflicting_orders.map(o => o.order_number).filter(Boolean) : []
    if (nums.length > 0) {
      return errorMessage(def.code, { orders: nums.join(', ') })
    }
    // No specific conflicting order in the payload — render a clean generic
    // phrase ("otro pedido") instead of an empty order number.
    return errorMessage(`${def.code}_no_order`)
  }
  return errorMessage(def.code)
}

const getLabelForErrors = errors => {
  // console.log("errors: ", errors)
  let errorString = ''
  errors.forEach(error => {
    const errorDef = getErrorDefinition(error.error_type)
    if (errorDef.hasConflictingOrders) {
      const conflictingOrderNumbers = []
      error.conflicting_orders.forEach(conflictingOrder => {
        conflictingOrderNumbers.push(conflictingOrder.order_number)
      })
      errorString += errorMessage(errorDef.code, { orders: conflictingOrderNumbers.join(', ') })
    } else {
      errorString += errorMessage(errorDef.code)
    }
  })
  return errorString
}

export const AssignOrdersGroupableIndicator = ({ groupable = null, errors = [] }) => {
  const [errorTooltipLabel, setErrorTooltipLabel] = useState(null)

  useEffect(() => {
    setErrorTooltipLabel(getLabelForErrors(errors))
  }, [errors])

  const ErrorTooltip = ({ children }) => {
    return <>{errorTooltipLabel !== null ? <Tooltip title={errorTooltipLabel}>{children}</Tooltip> : children}</>
  }

  return (
    <ErrorTooltip>
      <div className="text-center w-100">
        {groupable === null ? (
          <></>
        ) : groupable === true ? (
          <CheckCircleIcon className="h-4 w-4 inline text-green-700" />
        ) : (
          <TimesCircleIcon className="h-4 w-4 inline text-yellow-500" />
        )}
        {errors?.length > 0 && <ExclamationIcon className="w-4 h-4 text-red-700 inline" />}
      </div>
    </ErrorTooltip>
  )
}
